import { Scene } from 'framework/SceneManager/Scene';
import { Game } from 'framework/Game';
import { ScenesName } from 'js/scenes/scenes';

import { BubbleGameBoard } from '../bubbleCore/BubbleGameBoard';
import { BubbleGameBoardView } from '../bubbleCore/view/BubbleGameBoardView';

import { HexGridType } from '../bubbleCore/types';
import { Background } from 'js/scenes/common/Background';
import { ILevelData } from 'js/bubbleCore/levelParser/parseLevel';
import { Controller } from 'js/bubbleCore/Controller';

import { GameOverPopup } from 'js/scenes/ui/GameOverPopup';
import { WaitingScreen } from 'js/scenes/ui/WaitingScreen';

import { loadUserData } from 'js/sdk/loadUserData';
import { saveUserData } from 'js/sdk/saveUserData';
// import { logEvent } from 'js/sdk/logEvent';

import triggerGameStart from "js/sdk/triggerGameStart";
import triggerGameOver from "js/sdk/triggerGameOver";
import { LeaderboardPopup } from 'js/scenes/ui/LeaderboardPopup';
import { TweenManager } from 'framework/TweenManager/TweenManager';

let highscore: number | null = null;

const levelData: ILevelData = {
  config: {
    fireEvery: 10,
    availableColors: [
      {startSec: 0, colors: 3},
      {startSec: 30, colors: 4},
      {startSec: 60, colors: 5},
      {startSec: 90, colors: 6}
    ]
  },
  grid: {
    type: HexGridType.OddR,
    hexLimits: {x: 0, y: 0, width: 13, height: 30},
    asymetrical: true,
    rowsToFill: 6,
    scrollSpeed: 0.00288,
    fillScrollSpeed: 0.01,
    deathLineY: 27,
    scrollFill: 5,
  },
  popOutSpace: {
    gravity: 0.02,
    popOutVelRange: {left: -0.4, right: 0.4, top: -0.2, bottom: 0},
    pointZones: [
      { x: 0, score: 50 },
      { x: 0.2, score: 100 },
      { x: 0.4, score: 250 },
      { x: 0.6, score: 100 },
      { x: 0.8, score: 50 },
    ],
    pointZonesY: 28,
    bouncers: {
      y: 27.05,
      width: 0.02,
      height: 0.05,
    },
  },
  shooter: {
    bubbleSpeed: 1,
    positionY: 30,
    angleLimitDeg: 85,
  },
};

export class MainScene extends Scene {    
  private readonly bg: Background;
  private gameBoard: BubbleGameBoard;
  private gameBoardView: BubbleGameBoardView;
  private controller: Controller;
  private tweenManager: TweenManager;

  private autoUpdate: boolean = true;
  private gameStarted = false;

  constructor(game: Game) {
    super(game);

    // @ts-ignore
    window.scene = this;

    this.bg = this.root.addChild(new Background());
    this.gameBoard = new BubbleGameBoard(this.prepareLevel());
    this.tweenManager = new TweenManager();

    this.gameBoardView = new BubbleGameBoardView({
      gameBoard: this.gameBoard,
      hexSize: 27,
      scale: 1.2,
      verticalOffset: 1,
      soundManager: this.game.sounds,
    });
    this.game.tweens.create(this.gameBoardView.getRoot())
      .from({alpha: 0}, 700, this.game.tweens.easing.Sinusoidal.Out, true);

    this.controller = new Controller(
      this.gameBoard,
      this.gameBoardView,
      {
        onGameOver: async () => await this.gameOver(),
        mobile: window.ontouchstart !== undefined,
      }
    );

    this.root.addChild(this.gameBoardView.getRoot());
    this.loadScore();

    // let username = await loadUserData(['username']);
    this.gameBoardView.getLayout().leaderBoardButton.on('pointerdown', () => {
      this.game.sounds.play("button_click");
      this.disableBackgroundButtonsHandler(false);
      this.pauseGameLeaderboardScreen();
      try {
        const tweenMgr = this.game.tweens;
        this.gameBoardView.getLayout().shadow.visible = true;
        new LeaderboardPopup({
          parent: this.root,
          tweenMgr,
          unPauseGameLeaderboardScreen: this.unPauseGameLeaderboardScreen.bind(this)
        });
      } catch (e) {
        console.warn(e);
      }
    });
    // this.initDebug();
  }

  private disableBackgroundButtonsHandler(state) {
    this.gameBoardView.getLayout().leaderBoardButton.interactive = state;
    this.gameBoardView.getLayout().leaderBoardButton.buttonMode = state;
    this.gameBoardView.getLayout().soundButton.interactive = state;
    this.gameBoardView.getLayout().soundButton.buttonMode = state;
    this.gameBoardView.getLayout().musicButton.interactive = state;
    this.gameBoardView.getLayout().musicButton.buttonMode = state;
  }

  private prepareLevel(): ILevelData {
    const diff = Math.floor(this.game.screen.height - 1085) / 32;
    const level: ILevelData = JSON.parse(JSON.stringify(levelData));

    level.shooter.positionY += diff;
    level.popOutSpace.pointZonesY += diff;
    level.popOutSpace.bouncers.y += diff;
    level.grid.deathLineY += diff;

    level.grid.hexLimits.height += Math.floor(diff / 1.5);
    // level.grid.rowsToFill += Math.floor(diff / 1.5);
    // level.grid.scrollFill += Math.floor(diff / 1.5);

    return level;
  }

  private async loadScore(): Promise<void> {
    highscore = (await loadUserData(['highscore'])).highscore || 0;
  }

  private async updateScore(newScore: number): Promise<void> {
    if (highscore === null || newScore < highscore) return;
    highscore = newScore;
    saveUserData({highscore});
  }

  private async gameOver(dummyScore?: number) {
    this.disableBackgroundButtonsHandler(false);
    const score = dummyScore || this.gameBoard.getData().state.score;
    const tweenMgr = this.game.tweens;

    triggerGameOver(score);

    this.gameBoardView.getLayout().shadow.visible = true;

    new GameOverPopup({
      tweenMgr,
      parent: this.root,
      restart: this.restartGame.bind(this),
      score,
      highscore: highscore || 0,
    });

    this.updateScore(score);
  }

  private async pauseGameLeaderboardScreen() {
    window.game.leaderboardOpened = true;
    window.game.gamePause = true;
  }

  private async unPauseGameLeaderboardScreen() {
    this.disableBackgroundButtonsHandler(true);
    const tweenMgr = this.game.tweens;
    const waitingScreen = new WaitingScreen(this.root);
    waitingScreen.destroy();

    this.gameBoardView.getLayout().shadow.visible = false;
    window.game.leaderboardOpened = false;
    window.game.gamePause = false;
  }

  private async restartGame() {
    // logEvent('game_retry');
    const tweenMgr = this.game.tweens;
    const waitingScreen = new WaitingScreen(this.root);
    waitingScreen.destroy();

    // tweenMgr.create(this.gameBoardView.getRoot())
    //   .to({alpha: 0}, 400, tweenMgr.easing.Sinusoidal.Out, true);
    this.gameBoardView.getLayout().shadow.visible = false;
    this.game.scenes.startScene(ScenesName.Main);
  }

  update(elapsedMs: number) {
    if (!this.gameStarted && this.gameBoard.getData().state.firstShoot) {
      this.gameStarted = true;
      triggerGameStart();
      if (!this.game.sounds.music.playing()) this.game.sounds.music.play();
    }

    if (this.autoUpdate) {
      this.gameBoard.update(elapsedMs);
      this.gameBoardView.update(elapsedMs);
    }
    this.controller.update();
  }

  handleResize(size: [number, number]): void {
    this.bg.handleResize(size);
  }
}